import { defineResource } from '@tnn/core'
import { z } from 'zod'

/**
 * ใบคุมรถ (tms.manifest): what a trip's truck or a delivery run carries as it leaves a branch, kept as it was then — the
 * list the driver takes and the next branch checks what comes off against. Made from the trip's or the run's page by
 * the branch it leaves (`branchId`), one a stretch of the way: made again there once what is on board changed, the new
 * one takes the old one's place (`replaced`). Its number is the branch's (`BKK1-M6910-0001`), its time when the truck
 * left. Nothing in it is typed: the server writes it whole, and it is never edited — only cancelled, with a reason.
 */
export const manifestStatuses = ['draft', 'issued', 'replaced', 'cancelled'] as const
export type ManifestStatus = (typeof manifestStatuses)[number]
export const manifestKinds = ['trip', 'run'] as const
export type ManifestKind = (typeof manifestKinds)[number]

/** A goods or add-on service line of a bill as it went on the list: a charge line (a return's freight) is money only */
export interface ManifestLine {
  name: string
  qty: number | null
  unit: string | null
  amount: number
}

/** One bill as it went on the list: who, where, how many, who pays, its goods with their prices, where it comes off */
export interface ManifestBill {
  id: string
  number: string
  bookedAt: string
  senderName: string
  receiverName: string
  receiverDistrict: string | null
  payer: string
  pieces: number
  /** The trip's stop it comes off at (a run's: none) */
  drop: string | null
  lines: ManifestLine[]
}

/** A manifest's bills always by receiver, as Thai names sort (dictionary order), then by number */
export const byReceiver = (a: Pick<ManifestBill, 'receiverName' | 'number'>, b: Pick<ManifestBill, 'receiverName' | 'number'>) =>
  (a.receiverName ?? '').localeCompare(b.receiverName ?? '', 'th') || a.number.localeCompare(b.number)

export const manifest = defineResource({
  app: 'tms',
  name: 'manifest',
  path: 'manifests',
  // Made by the server from a trip or a run (POST /api/tms/trips/:id/manifests, /runs/:id/manifests)
  schema: z.object({}),
  fields: {
    number: { readOnly: true, search: true, list: { width: 170, priority: 1 } },
    /** When it was made: when the truck left the branch */
    issuedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    kind: { readOnly: true, type: 'select', options: manifestKinds, filter: true, list: { width: 110, priority: 2 } },
    tripId: { readOnly: true, type: 'ref', ref: { resource: 'tms.trip' }, filter: true, list: { width: 130, priority: 2 } },
    runId: { readOnly: true, type: 'ref', ref: { resource: 'tms.deliveryRun' }, filter: true, list: { width: 130, priority: 2 } },
    /** The branch it was made at, which the truck left */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 1 } },
    vehicleId: { readOnly: true, type: 'ref', ref: { resource: 'tms.vehicle' }, list: { width: 120, priority: 3, hidden: true } },
    driverId: { readOnly: true, type: 'ref', ref: { resource: 'tms.driver' }, list: { width: 160, priority: 3, hidden: true } },
    /** A trip's way then, from its origin (branch ids); a run's: its branch */
    route: { readOnly: true, type: 'multiselect', ref: { resource: 'company.branch' }, list: false },
    bills: { readOnly: true, type: 'integer', list: { width: 80, align: 'end', priority: 1 } },
    pieces: { readOnly: true, type: 'integer', list: { width: 84, align: 'end', priority: 2 } },
    /** The bills as they went on it (`ManifestBill[]`) */
    lines: { readOnly: true, type: 'json', list: false, export: false },
    status: {
      readOnly: true,
      type: 'select',
      options: manifestStatuses,
      filter: true,
      tones: { draft: 'neutral', issued: 'success', replaced: 'neutral', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    /** The one made after it for the same stretch, in its place */
    replacedById: { readOnly: true, type: 'ref', ref: { resource: 'tms.manifest' }, list: false },
    issuedBy: { readOnly: true, list: { priority: 3, hidden: true } },
  },
  title: 'number',
  columns: ['number', 'issuedAt', 'kind', 'tripId', 'runId', 'branchId', 'bills', 'pieces', 'status', 'issuedBy'],
  defaultSort: '-issuedAt',
  // Made by whoever loads the trip or the run there (their `scan`): nobody creates one by hand
  actions: ['read', 'export', 'print'],
  branchFields: ['branchId'],
  submittable: { cancelReason: 'required' },
  card: { title: 'number', subtitle: 'branchId', meta: ['issuedAt', 'bills'], status: 'status' },
})
